import { guessImageMediaType } from './screenshot-upload.util';
import type { SendpulseUIMessage } from './sendpulse-chat-transport';

/**
 * A message as landings-service stores it (#73738): the OpenAI chat-completions wire shape, so the
 * history stays readable by anything that speaks it. The server truncates by collapsing a `tool`
 * message's `content` to `"..."` and a call's `arguments` to `"{}"`.
 */
export type StoredChatContentPart = { type: 'text'; text: string } | { type: 'image_url'; image_url: { url: string } };

export type StoredToolCall = { id: string; type: 'function'; function: { name: string; arguments: string } };

export type StoredChatMessage =
    | { role: 'system'; content: string | null }
    | { role: 'user'; content: string | StoredChatContentPart[] | null }
    | { role: 'assistant'; content: string | null; tool_calls?: StoredToolCall[] }
    | { role: 'tool'; tool_call_id: string; content: string | null };

/** The server's own budget is 1 MB of PHP-encoded JSON, which escapes `/` and so runs longer than ours. */
export const STORED_BATCH_MAX_BYTES = 900_000;

const COLLAPSED_CONTENT = '...';
const COLLAPSED_ARGUMENTS = '{}';

type Part = SendpulseUIMessage['parts'][number];
type ToolPart = Extract<Part, { toolCallId: string }>;

// Not `ai`'s own `isToolUIPart` / `getToolName` / `generateId`: that package ships as ESM, which this
// app's Jest does not transform, and these are the only three things this file needs from it.
const isToolPart = (part: Part): part is ToolPart => part.type === 'dynamic-tool' || part.type.startsWith('tool-');
const toolName = (part: ToolPart): string =>
    part.type === 'dynamic-tool' ? (part as { toolName: string }).toolName : part.type.slice('tool-'.length);
const generateId = (): string => crypto.randomUUID();

/**
 * UI messages → stored ones. An assistant reply is one UI message spanning every step of its tool
 * loop; stored, it is one assistant message per step followed by that step's tool results — the
 * shape the model saw it in. A call with no result yet is dropped: stored, it would be a dangling
 * `tool_calls` entry the API refuses.
 */
export function toStoredChatMessages(messages: readonly SendpulseUIMessage[]): StoredChatMessage[] {
    return messages.flatMap((message): StoredChatMessage[] => {
        switch (message.role) {
            case 'user':
                return [{ role: 'user', content: userContent(message.parts) }];
            case 'assistant':
                return splitSteps(message.parts).flatMap(storedStep);
            default:
                return [];
        }
    });
}

/**
 * Stored messages → UI ones, each flagged `restored`. Consecutive assistant and tool messages fold
 * back into the one reply they came from; a call whose result was lost is left out, as above.
 */
export function fromStoredChatMessages(stored: readonly StoredChatMessage[]): SendpulseUIMessage[] {
    const messages: SendpulseUIMessage[] = [];
    let reply: SendpulseUIMessage | null = null;
    const pendingCalls = new Map<string, ToolPart>();

    const closeReply = (): void => {
        if (reply) {
            reply.parts = reply.parts.filter((part) => !isToolPart(part) || part.state === 'output-available');
            messages.push(reply);
        }

        reply = null;
        pendingCalls.clear();
    };

    for (const message of stored) {
        if (message.role === 'user') {
            closeReply();
            messages.push({
                id: generateId(),
                role: 'user',
                parts: restoredUserParts(message.content),
                metadata: { restored: true },
            });
            continue;
        }

        if (message.role === 'assistant') {
            reply ??= { id: generateId(), role: 'assistant', parts: [], metadata: { restored: true } };
            reply.parts.push({ type: 'step-start' });

            if (message.content) {
                reply.parts.push({ type: 'text', text: message.content, state: 'done' });
            }

            for (const call of message.tool_calls ?? []) {
                const part = {
                    type: `tool-${call.function.name}`,
                    toolCallId: call.id,
                    state: 'input-available',
                    input: parseJson(call.function.arguments) ?? {},
                } as ToolPart;

                reply.parts.push(part);
                pendingCalls.set(call.id, part);
            }

            continue;
        }

        if (message.role === 'tool') {
            const part = pendingCalls.get(message.tool_call_id);

            if (part) {
                Object.assign(part, {
                    state: 'output-available',
                    output: parseJson(message.content ?? '') ?? message.content,
                });
            }
        }
    }

    closeReply();

    return messages;
}

/**
 * Brings a batch under the server's per-request budget the way the server itself truncates —
 * oldest tool payloads first, the text the user and the assistant wrote never. Hands the batch back
 * unchanged when it already fits; one that still does not fit after that is the server's to refuse.
 */
export function fitStoredBatch(
    messages: readonly StoredChatMessage[],
    maxBytes = STORED_BATCH_MAX_BYTES
): StoredChatMessage[] {
    const batch = [...messages];
    let total = batch.reduce((sum, message) => sum + byteLength(message), 0);

    for (let index = 0; index < batch.length && total > maxBytes; index++) {
        const collapsed = collapse(batch[index]);

        total += byteLength(collapsed) - byteLength(batch[index]);
        batch[index] = collapsed;
    }

    return batch;
}

/**
 * The model's copy of a conversation that began with restored messages: a note at the head of the
 * first new user message, saying that ids from before it may be gone. Messages are left alone when
 * nothing was restored, or when no new user message follows the restored ones.
 */
export function markRestoredHistory(messages: readonly SendpulseUIMessage[], note: string): SendpulseUIMessage[] {
    const lastRestored = messages.findLastIndex((message) => message.metadata?.restored);
    const firstNew = messages.findIndex((message, index) => index > lastRestored && message.role === 'user');

    if (lastRestored < 0 || firstNew < 0) {
        return [...messages];
    }

    return messages.map((message, index) =>
        index === firstNew ? { ...message, parts: [{ type: 'text', text: note }, ...message.parts] } : message
    );
}

function userContent(parts: readonly Part[]): string | StoredChatContentPart[] {
    const content = parts.flatMap((part): StoredChatContentPart[] => {
        if (part.type === 'text') {
            return [{ type: 'text', text: part.text }];
        }

        return part.type === 'file' ? [{ type: 'image_url', image_url: { url: part.url } }] : [];
    });

    return content.every((part) => part.type === 'text') ? content.map((part) => part.text).join('\n') : content;
}

function restoredUserParts(content: string | StoredChatContentPart[] | null): Part[] {
    if (!content) {
        return [];
    }

    if (typeof content === 'string') {
        return [{ type: 'text', text: content }];
    }

    return content.map(
        (part): Part =>
            part.type === 'text'
                ? { type: 'text', text: part.text }
                : { type: 'file', mediaType: guessImageMediaType(part.image_url.url), url: part.image_url.url }
    );
}

function splitSteps(parts: readonly Part[]): Part[][] {
    return parts.reduce<Part[][]>(
        (steps, part) => {
            if (part.type === 'step-start') {
                return steps.at(-1)?.length ? [...steps, []] : steps;
            }

            steps.at(-1)?.push(part);

            return steps;
        },
        [[]]
    );
}

function storedStep(parts: readonly Part[]): StoredChatMessage[] {
    const text = parts
        .filter((part) => part.type === 'text')
        .map((part) => part.text.trim())
        .filter(Boolean)
        .join('\n\n');
    const calls = parts.filter(
        (part): part is ToolPart =>
            isToolPart(part) && (part.state === 'output-available' || part.state === 'output-error')
    );

    if (!text && !calls.length) {
        return [];
    }

    return [
        {
            role: 'assistant',
            content: text || null,
            ...(calls.length
                ? {
                      tool_calls: calls.map((part) => ({
                          id: part.toolCallId,
                          type: 'function' as const,
                          function: { name: toolName(part), arguments: JSON.stringify(part.input ?? {}) },
                      })),
                  }
                : {}),
        },
        ...calls.map(
            (part): StoredChatMessage => ({ role: 'tool', tool_call_id: part.toolCallId, content: toolResult(part) })
        ),
    ];
}

/** A failed call is stored as the `{ ok: false }` shape the tools report failures in, so it reads back as one. */
function toolResult(part: ToolPart): string {
    if (part.state === 'output-error') {
        return JSON.stringify({ ok: false, error: part.errorText });
    }

    return typeof part.output === 'string' ? part.output : JSON.stringify(part.output ?? null);
}

function collapse(message: StoredChatMessage): StoredChatMessage {
    if (message.role === 'tool') {
        return { ...message, content: COLLAPSED_CONTENT };
    }

    if (message.role === 'assistant' && message.tool_calls?.length) {
        return {
            ...message,
            tool_calls: message.tool_calls.map((call) => ({
                ...call,
                function: { ...call.function, arguments: COLLAPSED_ARGUMENTS },
            })),
        };
    }

    return message;
}

function byteLength(message: StoredChatMessage): number {
    return new TextEncoder().encode(JSON.stringify(message)).length;
}

function parseJson(value: string): unknown {
    try {
        return JSON.parse(value);
    } catch {
        return undefined;
    }
}
